// packages import
import { createContext, FC, Reducer, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'

// others
import { Bill, BillingType, BillStatus, useFetchFinalBillsLazyQuery } from '../../generated'
import { FINAL_BILL_STATUSES, GRAPHQL_QUERY_POLICY, VOID_BILL_STATUSES } from '../constants'
import { ChildrenType } from '../interfaceTypes'

import { AuthContext } from './AuthContext'
import { AppContext } from './AppContext'
import { ActionType } from './AppContextReducer'
import {
  FinalBillsAction,
  FinalBillsActionType,
  FinalBillsFilters,
  finalBillsInitialState,
  finalBillsReducer,
  FinalBillsState
} from '../components/billing/finalBills/FinalBillsReducer'
import { useParams, useSearchParams } from 'react-router-dom'

const PAGE_LIMIT = 20
export interface FinalBillsContextType extends FinalBillsState {
  dispatch: React.Dispatch<FinalBillsAction>
  filters: FinalBillsFilters
  refetch: () => void
  setFilters: (filters: FinalBillsFilters) => void
}

export const FinalBillsContext = createContext<FinalBillsContextType>({
  ...finalBillsInitialState,
  filters: {
    searchTerm: ''
  },
  dispatch: () => {
    return
  },
  refetch: () => {
    return
  },
  setFilters: () => {
    return
  }
})

export const FinalBillsContextProvider: FC<ChildrenType> = ({ children }): JSX.Element => {
  const { isLoggedIn } = useContext(AuthContext)
  const { dispatch: appDispatch } = useContext(AppContext)
  const [state, dispatch] = useReducer<Reducer<FinalBillsState, FinalBillsAction>>(
    finalBillsReducer,
    finalBillsInitialState
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const { clientNo } = useParams<{ clientNo?: string }>()

  const currentPage = useMemo(() => parseInt(searchParams.get('currentPage') || '1'), [searchParams])

  const filters = useMemo<FinalBillsFilters>(() => {
    const attorneyId = searchParams.get('attorneyId') || undefined
    const lawTypeId = searchParams.get('lawTypeId') || undefined
    const caseType = (searchParams.get('caseType') as BillingType) || undefined
    const status = (searchParams.get('status') as BillStatus | 'all') || undefined
    const searchTerm = searchParams.get('search') || ''
    const billDeliveryType = searchParams.get('billDeliveryType') || undefined
    const sentStatusParam = searchParams.get('sentStatus')
    const sentStatus = sentStatusParam === 'true' ? true : sentStatusParam === 'false' ? false : undefined
    const isDelinquentBills = searchParams.get('isDelinquentBills') === 'true'
    const onlyWithTrust = searchParams.get('onlyWithTrust') === 'true'
    const onlyWithCredit = searchParams.get('onlyWithCredit') === 'true'

    return {
      attorneyId,
      lawTypeId,
      caseType,
      status,
      searchTerm,
      billDeliveryType,
      sentStatus,
      isDelinquentBills,
      onlyWithTrust,
      onlyWithCredit
    }
  }, [searchParams])

  const [fetchFinalBillsQuery, { loading, refetch }] = useFetchFinalBillsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {
      dispatch({ type: FinalBillsActionType.SET_FINAL_BILLS, bills: [] })
      dispatch({ type: FinalBillsActionType.SET_LOADING, loading: false })
      appDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },
    onCompleted(data) {
      if (data?.fetchFinalBills) {
        const { bills, pagination } = data.fetchFinalBills

        if (bills) {
          dispatch({ type: FinalBillsActionType.SET_FINAL_BILLS, bills: bills as Bill[] })
        }

        if (pagination) {
          const { totalPages, totalCount } = pagination
          totalPages && appDispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: totalPages })
          totalCount && appDispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: totalCount })
        }

        dispatch({ type: FinalBillsActionType.SET_LOADING, loading: false })
        appDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
      }
    }
  })

  useEffect(() => {
    if (isLoggedIn) {
      dispatch({ type: FinalBillsActionType.SET_LOADING, loading: true })
      let status: BillStatus[] = []

      if (filters.isDelinquentBills) {
        // Same definition as the DELINQUENT_BILLS print job: a matter carrying an unpaid balance
        // more than 30 days old, counting balances rolled forward onto later bills (the server
        // applies it; see matterIsDelinquentSql).
        // `Billed` belongs here — a bill that was finalized but never advanced to Sent To Client is
        // still owed, and delivery is tracked by lastSentAt (the Sent To Client column / sentStatus
        // filter), not by status. Leaving it out hid the bulk of the delinquent set and made this
        // page disagree with the print job. The due-date cutoff itself is applied server-side via
        // onlyPastDue below.
        status = [BillStatus.Billed, BillStatus.SentToClient, BillStatus.PartiallyPaid]
      } else if (filters.status === 'all') {
        const voidBillStatuses = VOID_BILL_STATUSES.filter(
          status => status !== BillStatus.Void && status !== BillStatus.Archived
        )
        status = [...FINAL_BILL_STATUSES, ...voidBillStatuses]
      } else if (typeof filters.status === 'string') {
        status = [filters.status as BillStatus]
      }

      fetchFinalBillsQuery({
        variables: {
          fetchFinalBillsInput: {
            searchTerm: filters.searchTerm,
            page: currentPage,
            limit: PAGE_LIMIT,
            attorneyId: filters.attorneyId,
            lawTypeId: filters.lawTypeId,
            caseType: filters.caseType,
            billDeliveryType: filters.billDeliveryType,
            sentStatus: filters.sentStatus,
            clientNumber: clientNo,
            status,
            onlyWithTrust: filters.onlyWithTrust,
            onlyWithCredit: filters.onlyWithCredit,
            onlyPastDue: filters.isDelinquentBills
          }
        }
      })
    }
  }, [filters, currentPage, isLoggedIn, appDispatch, fetchFinalBillsQuery, clientNo])

  useEffect(() => {
    // Set current page from query params
    appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage })

    return () => {
      appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
    // eslint-disable-next-line
  }, [])

  const setFilters = useCallback(
    (newFilters: FinalBillsFilters) => {
      setSearchParams(params => {
        if ('searchTerm' in newFilters) {
          if (newFilters.searchTerm) {
            params.set('search', newFilters.searchTerm)
          } else {
            params.delete('search')
          }
        }

        if ('attorneyId' in newFilters) {
          if (newFilters.attorneyId) {
            params.set('attorneyId', newFilters.attorneyId)
          } else {
            params.delete('attorneyId')
          }
        }

        if ('lawTypeId' in newFilters) {
          if (newFilters.lawTypeId) {
            params.set('lawTypeId', newFilters.lawTypeId)
          } else {
            params.delete('lawTypeId')
          }
        }

        if ('caseType' in newFilters) {
          if (newFilters.caseType) {
            params.set('caseType', newFilters.caseType)
          } else {
            params.delete('caseType')
          }
        }

        if ('status' in newFilters) {
          if (newFilters.status) {
            params.set('status', newFilters.status)
          } else {
            params.delete('status')
          }
        }

        if ('billDeliveryType' in newFilters) {
          if (newFilters.billDeliveryType) {
            params.set('billDeliveryType', newFilters.billDeliveryType)
          } else {
            params.delete('billDeliveryType')
          }
        }

        if ('sentStatus' in newFilters) {
          if (typeof newFilters.sentStatus === 'boolean') {
            params.set('sentStatus', String(newFilters.sentStatus))
          } else {
            params.delete('sentStatus')
          }
        }

        if ('isDelinquentBills' in newFilters) {
          if (newFilters.isDelinquentBills) {
            params.set('isDelinquentBills', newFilters.isDelinquentBills.toString())
          } else {
            params.delete('isDelinquentBills')
          }
        }

        if ('onlyWithTrust' in newFilters) {
          if (newFilters.onlyWithTrust) {
            params.set('onlyWithTrust', 'true')
          } else {
            params.delete('onlyWithTrust')
          }
        }

        if ('onlyWithCredit' in newFilters) {
          if (newFilters.onlyWithCredit) {
            params.set('onlyWithCredit', 'true')
          } else {
            params.delete('onlyWithCredit')
          }
        }

        // Reset page to 1 when filters change
        params.set('currentPage', '1')
        appDispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })

        return params
      })
    },
    [setSearchParams, appDispatch]
  )
  const contextValue = useMemo(
    () => ({
      ...state,
      filters,
      loading: loading || state.loading,
      dispatch,
      refetch,
      setFilters
    }),
    [state, loading, refetch, setFilters, filters]
  )

  return <FinalBillsContext.Provider value={contextValue}>{children}</FinalBillsContext.Provider>
}
